/**
 * 系统设置 · 通知策略页：展示关键运营事件的通知规则（退款率异常 / 风控封禁 / 反馈 SLA 等）。
 * dev-docs 无通知策略端点（§五），故所有开关为只读展示（disabled），不可修改也不持久化。
 */
import { Icons } from '~/lib/icons';
import { Switch } from '~/ui/Switch';
import { NotInScopeNotice, SectionHead } from './settings-shared';

// ---------- Notification strategy（NOT-in-scope：后端无通知策略端点，§五）----------
interface NotifyRule {
  id: string;
  name: string;
  desc: string;
  threshold: string;
  channel: string[];
  on: boolean;
}

export default function NotificationsPage() {
  // 静态展示原型的通知策略项，但 Switch 全 disabled（无后端端点，§五）；不接 onChange、不假成功
  const RULES: NotifyRule[] = [
    {
      id: 'refund_surge',
      name: '退款率异常',
      desc: '被动退款率超过阈值连续 2 天',
      threshold: '3.0%',
      channel: ['邮件', 'Slack'],
      on: true,
    },
    {
      id: 'risk_ban',
      name: '风控自动封禁',
      desc: '系统自动封禁账户时',
      threshold: '—',
      channel: ['Slack'],
      on: true,
    },
    {
      id: 'fb_sla',
      name: '反馈 SLA 临期',
      desc: '反馈未回复超过 6 小时',
      threshold: '6 小时',
      channel: ['邮件'],
      on: true,
    },
    {
      id: 'rev_drop',
      name: '收入异常下跌',
      desc: '日收入环比下降超过阈值',
      threshold: '20%',
      channel: ['邮件', 'Slack'],
      on: true,
    },
    {
      id: 'integration',
      name: '集成异常',
      desc: '任一集成连续失败 3 次以上',
      threshold: '—',
      channel: ['邮件', 'Slack'],
      on: true,
    },
    {
      id: 'weekly',
      name: '周报',
      desc: '每周一 09:00 自动发送',
      threshold: '—',
      channel: ['邮件'],
      on: false,
    },
  ];
  return (
    <>
      <SectionHead title="通知策略" sub="选择需要被通知的关键运营事件" />
      <NotInScopeNotice text="通知策略开关 dev-docs 无对应端点 → 以下开关为只读展示（disabled），不可修改也不持久化。详见 docs/plans §五。" />
      <div className="card" style={{ overflow: 'hidden' }}>
        {RULES.map((r, i) => (
          <div
            key={r.id}
            className="hstack"
            style={{
              padding: 14,
              gap: 12,
              borderBottom: i < RULES.length - 1 ? '1px solid var(--border)' : 0,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 6,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icons.IconBell size={15} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 12.5 }}>{r.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>{r.desc}</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              阈值 <span className="mono strong">{r.threshold}</span>
            </div>
            <div className="hstack" style={{ gap: 4 }}>
              {r.channel.map((c) => (
                <span key={c} className="chip" style={{ fontSize: 10 }}>
                  {c}
                </span>
              ))}
            </div>
            <Switch on={r.on} disabled />
          </div>
        ))}
      </div>
    </>
  );
}
